import React, { ComponentPropsWithoutRef } from 'react';
import { useBreakpoint, Box, forwardRef } from '@landr/maestro';

// Note: Maestro doesn't export Breakpoint
type Breakpoint = 'xs' | 'sm' | 'ms' | 'md' | 'lg' | 'xl' | 'xxl';

type HiddenRule = '>' | '<=';

type BoxComponent = typeof Box;

interface HiddenByCssProps
    extends Omit<ComponentPropsWithoutRef<BoxComponent>, 'ref'> {
    isHidden: boolean;
}

interface HiddenProps
    extends Omit<ComponentPropsWithoutRef<BoxComponent>, 'ref'> {
    at: Breakpoint;
    operator?: HiddenRule;
    hideWithStyle?: boolean;
}

/**
 * A component that can be hidden using CSS display property
 */
export const HiddenByCss = forwardRef<HiddenByCssProps, 'div'>(
    ({ isHidden, as = Box, children, ...props }, ref) => {
        return (
            <Box
                ref={ref}
                as={as}
                display={isHidden ? 'none' : undefined}
                {...props}
            >
                {children}
            </Box>
        );
    },
);

/**
 * Hidden hide his children on demand
 * By default we hide using CSS - it's the preferred method to minimize DOM manipulation
 * But when hideWidthStyle is false, we remove the children from the DOM by returning null
 */
export const Hidden = forwardRef<HiddenProps, 'div'>(
    (
        {
            at,
            as = Box,
            operator = '<=',
            hideWithStyle = true,
            children,
            color,
            ...props
        },
        ref,
    ) => {
        let isHidden = false;
        const AsComponent = as;
        const isBreakpoint = useBreakpoint(at);

        if (operator === '<=') {
            isHidden = isBreakpoint;
        } else {
            isHidden = !isBreakpoint;
        }

        // If not using style-based hiding and component should be hidden, return null
        if (!hideWithStyle && isHidden) {
            return null;
        }

        // If not using style-based hiding and component should be visible, render normally
        if (!hideWithStyle && !isHidden) {
            return <AsComponent {...props}>{children}</AsComponent>;
        }

        // Using style-based hiding
        return (
            <HiddenByCss ref={ref} as={as} isHidden={isHidden} {...props}>
                {children}
            </HiddenByCss>
        );
    },
);
